import { useState, useEffect, useCallback, useMemo, memo, useRef } from 'react'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import { useQuery } from '@tanstack/react-query'
import TripDetail from './TripDetailModal'
import TripListModal from './TripListModal'
import GenericListFilter from './GenericListFilter'
import { calculateFuelUsedLiters, calculateTripDurationMinutes } from '../helpers/calculateConsumption'
import TableSortLabel from '@mui/material/TableSortLabel'
import ResetTableButton from '../components/ResetTableButton'
import useGenericListFilter from '../hooks/useGenericListFilter'
import useGenericTextSearch from '../hooks/useGenericTextSearch'
import useSharedModalUiState from '../hooks/useSharedModalUiState'
import FilterToggleButton from '../components/FilterToggleButton'
import GenericSearchToggle from '../components/GenericSearchToggle'
import GenericAddButton from '../components/GenericAddButton'
import RefreshButton from '../components/RefreshButton'
import TripAddModal from './TripAddModal'
import DetailButton from '../components/DetailButtonModal'
import RefuelButtonModal from '../components/RefuelButtonModal'
import ReceiptDetailModal from './ReceiptDetailModal'
import TripDeleteModal from './TripDeleteModal'
import deleteIcon from '../assets/images/icons/delete.png'
import refuelingIcon from '../assets/images/icons/refueling.png'
import detailIcon from '../assets/images/icons/detail.png'
import noGpsIcon from '../assets/images/icons/no_gps.png'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'

import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable'

import { CSS } from '@dnd-kit/utilities'

const tripDetailFields = [
  ['date', 'TRIPLOG.TRIPS.TABLE.date'],
  ['vehicleId', 'TRIPLOG.TRIPS.TABLE.vehicleId'],
  ['spz', 'vehicle_license_plate'],
  ['VIN', 'TRIPLOG.TRIPS.TABLE.VIN'],
  ['driver', 'TRIPLOG.TRIPS.TABLE.driver'],
  ['startTimeIso', 'TRIPLOG.TRIPS.TABLE.startTimeIso'],
  ['startPlace', 'TRIPLOG.TRIPS.TABLE.startPlace'],
  ['endTimeIso', 'TRIPLOG.TRIPS.TABLE.endTimeIso'],
  ['endPlace', 'TRIPLOG.TRIPS.TABLE.endPlace'],
  ['odoStart', 'TRIPLOG.TRIPS.TABLE.odoStart'],
  ['odoEnd', 'TRIPLOG.TRIPS.TABLE.odoEnd'],
  ['distanceKm', 'TRIPLOG.TRIPS.TABLE.distanceKm'],
  ['duration', 'TRIPLOG.TRIPS.TABLE.durationMinutes'],
  ['purpose', 'TRIPLOG.TRIPS.TABLE.purpose'],
  ['refuel', 'TRIPLOG.TRIPS.TABLE.receiptId'],
  ['avgSpeedKmh', 'TRIPLOG.TRIPS.TABLE.avgSpeedKmh'],
  ['fuelUsed', 'TRIPLOG.TRIPS.TABLE.fuelUsedLiters'],
  ['model', 'TRIPLOG.TRIPS.TABLE.model'],
  ['typ_variant_verzia', 'TRIPLOG.TRIPS.TABLE.typ_variant_verzia'],
  ['note', 'TRIPLOG.TRIPS.TABLE.note'],
  ['mode', 'TRIPLOG.TRIPS.TABLE.mode'],
  ['gpsStart', 'TRIPLOG.TRIPS.TABLE.gpsStart'],
  ['gpsEnd', 'TRIPLOG.TRIPS.TABLE.gpsEnd']
]

const DEFAULT_COLUMNS = [
  { key: 'tripNumber', labelKey: '' },
  { key: 'date', labelKey: 'TRIPLOG.TRIPS.TABLE.date' },
  { key: 'vehicleId', labelKey: 'TRIPLOG.TRIPS.TABLE.vehicleId' },
  { key: 'spz', labelKey: 'vehicle_license_plate' },
  { key: 'startTimeIso', labelKey: 'TRIPLOG.TRIPS.TABLE.startTimeIso' },
  { key: 'startPlace', labelKey: 'TRIPLOG.TRIPS.TABLE.startPlace' },
  { key: 'endTimeIso', labelKey: 'TRIPLOG.TRIPS.TABLE.endTimeIso' },
  { key: 'endPlace', labelKey: 'TRIPLOG.TRIPS.TABLE.endPlace' },
  { key: 'distanceKm', labelKey: 'TRIPLOG.TRIPS.TABLE.distanceKm' },
  { key: 'driver', labelKey: 'TRIPLOG.TRIPS.TABLE.driver' },
  { key: 'purpose', labelKey: 'TRIPLOG.TRIPS.TABLE.purpose' }
]

const formatDateSk = (value) => {
  if (!value) return '-'
  return new Date(value).toLocaleDateString('sk-SK')
}

const formatTimeSk = (value) => {
  if (!value) return '-'
  return new Date(value).toLocaleTimeString('sk-SK', {
    hour: '2-digit',
    minute: '2-digit'
  })
}

const formatDateTimeSk = (value) => {
  if (!value) return '-'
  return new Date(value).toLocaleString('sk-SK', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function SortableHeaderCell({
  col,
  orderBy,
  order,
  handleSort,
  colWidths,
  handleMouseDown,
  t
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: col.key })

  const customWidth = colWidths[col.key]

const style = {
  transform: CSS.Transform.toString(transform),
  transition,
  ...(col.key === 'tripNumber'
    ? {
        flex: '0 0 auto',
        width: 50,
        minWidth: 50,
        maxWidth: 50
      }
    : customWidth
      ? {
          width: customWidth,
          minWidth: customWidth,
          maxWidth: customWidth
        }
      : {
          flex: '1 1 0',
          minWidth: 0
        }),
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    paddingRight: 8,
    boxSizing: 'border-box'
  }

  return (
    <div ref={setNodeRef} style={style}>
      <span
        {...attributes}
        {...listeners}
        style={{
          cursor: 'grab',
          marginRight: 6,
          color: '#999'
        }}
      >
        ⠿
      </span>

      <TableSortLabel
        active={orderBy === col.key || (orderBy === 'id' && col.key === 'tripNumber')}
        direction={orderBy === col.key || (orderBy === 'id' && col.key === 'tripNumber') ? order : 'asc'}
        onClick={() => handleSort(col.key)}
      >
        {t(col.labelKey)}
      </TableSortLabel>

      <div
        style={{
          position: 'absolute',
          right: 4,
          top: '20%',
          bottom: '20%',
          width: 1,
          background: '#9e9e9e'
        }}
      />

      <div
        onMouseDown={(e) => handleMouseDown(e, col.key)}
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: 6,
          cursor: 'col-resize'
        }}
      />
    </div>
  )
}

const TripRow = memo(function TripRow({
  trip,
  columns,
  colWidths,
  rowFontSize,
  isActive,
  isHovered,
  detailLabel,
  onDeleteTrip,
  detailColWidth,
  getTripVehicleLabel,
  getTripVehicleSpz,
  onOpenTrip,
  onHoverTrip,
  onClearHover,
  onRefuelClick,
setShowFullscreenHint,
tooltipTimerRef,
setCursorPos,
t,
}) {
  return (
    <div
      onClick={() => onOpenTrip(trip, true)}
      onMouseEnter={(e) => {
        if (e.target.closest('.trip-detail-button')) return
        onHoverTrip(trip)
      }}
      onMouseMove={(e) => {
        if (e.target.closest('.trip-detail-button')) {
          onClearHover()
        } else {
          onHoverTrip(trip)
        }
      }}
      onMouseLeave={(e) => {
        const related = e.relatedTarget
        if (related && related.closest && related.closest('.vehicle-tooltip')) return
        onClearHover()
      }}
style={{
  display: 'flex',
  padding: '6px 2px 6px 12px',
  borderTop: '1px solid #ececec',
  alignItems: 'center',
  borderLeft: !trip?.gpsStart && !trip?.gpsEnd
    ? '3px solid #06857a'
    : '3px solid transparent',

  backgroundColor:
    isActive ? '#e6f4f1' :
    isHovered ? '#f3fafa' :
    (!trip?.gpsStart && !trip?.gpsEnd)
      ? '#f6ffff'
      : '#fff',

  cursor: 'pointer',
  transition: 'background-color 0.15s ease',
  fontSize: rowFontSize
}}
    >
      {columns.map((col) => (
        <div
          key={col.key}
style={{
  ...(col.key === 'tripNumber'
    ? {
        flex: '0 0 auto',
        width: 50
      }
    : colWidths[col.key]
      ? {
          width: colWidths[col.key],
          minWidth: colWidths[col.key],
          maxWidth: colWidths[col.key]
        }
      : {
          flex: '1 1 0',
          minWidth: 0
        }),
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  textAlign: col.key === 'distanceKm' ? 'right' : 'left',
  paddingRight: col.key === 'distanceKm' ? 8 : 0
}}
        >
{col.key === 'tripNumber'
  ? (
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}
      >
        <span>{trip.tripNumber}</span>

        {!trip?.gpsStart && !trip?.gpsEnd && (
          <img
            src={noGpsIcon}
            alt="no-gps"
            style={{
              width: 20,
              height: 20
            }}
          />
        )}
      </span>
    )
  : col.key === 'vehicleId'
    ? getTripVehicleLabel(trip)
  : col.key === 'spz'
    ? getTripVehicleSpz(trip)
  : col.key === 'date'
    ? formatDateSk(trip.date)
              : col.key === 'startTimeIso'
                ? formatTimeSk(trip.startTimeIso)
: col.key === 'endTimeIso'
  ? (
      <span style={{ display: 'inline-block', marginLeft: 10 }}>
        {formatTimeSk(trip.endTimeIso)}
      </span>
    )
: typeof trip[col.key] === 'object'
  ? '-'
  : col.key === 'distanceKm'
    ? (
        <span style={{ display: 'inline-block', transform: 'translateX(-12px)' }}>
          {trip[col.key] ?? '-'}
        </span>
      )
    : trip[col.key] ?? '-'}
        </div>
      ))}
<div
  onClick={(e) => e.stopPropagation()}
  onMouseDown={(e) => e.stopPropagation()}
  onMouseEnter={() => {
    if (!(trip?.receiptId || trip?.receipt_id)) return

    const timer = setTimeout(() => {
      setShowFullscreenHint(t('TRIPLOG.RECEIPTS.DETAIL'))
    }, 600)

    tooltipTimerRef.current = timer
  }}
  onMouseLeave={() => {
    if (tooltipTimerRef.current) {
  clearTimeout(tooltipTimerRef.current)
  tooltipTimerRef.current = null
}
    setShowFullscreenHint(false)
  }}
  onMouseMove={(e) => {
    if (!(trip?.receiptId || trip?.receipt_id)) return
    setCursorPos({ x: e.clientX, y: e.clientY })
  }}
  style={{
    flex: `0 0 ${detailColWidth}px`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: -2
  }}
>
  <RefuelButtonModal
    active={Boolean(trip?.receiptId || trip?.receipt_id)}
    onClick={() => onRefuelClick(trip)}
  />
</div>
      <div
        style={{
          flex: `0 0 ${detailColWidth}px`,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
<DetailButton
  onClick={(e) => {
    e.stopPropagation()
    onOpenTrip(trip, false)
  }}
/>
      </div>
<div
  style={{
    flex: `0 0 ${detailColWidth}px`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: -4,
    marginRight: -6
  }}
>
<button
  style={{
    border: '1.5px solid #a33',
    background: '#f5f5f5',
    borderRadius: 6,
    height: 28,
    width: 35,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 1px 8px rgba(0,0,0,0.30)',
    transition: 'transform 0.05s ease, box-shadow 0.1s ease',
    outline: 'none'
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.background = '#f3bcbc'
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.background = '#f5f5f5'
    e.currentTarget.style.transform = 'scale(1)'
    e.currentTarget.style.boxShadow = '0 1px 8px rgba(0,0,0,0.30)'
  }}
  onMouseDown={(e) => {
    e.currentTarget.style.transform = 'scale(0.96)'
    e.currentTarget.style.boxShadow = '0 0px 4px rgba(0,0,0,0.25)'
  }}
  onMouseUp={(e) => {
    e.currentTarget.style.transform = 'scale(1)'
    e.currentTarget.style.boxShadow = '0 1px 8px rgba(0,0,0,0.30)'
    e.currentTarget.blur()
  }}
  onClick={(e) => {
    e.stopPropagation()
    onDeleteTrip(trip)
  }}
>
  <img
    src={deleteIcon}
    alt="delete"
    style={{
      width: 19,
      height: 19,
      pointerEvents: 'none'
    }}
  />
</button>
</div>
    </div>
  )
}, (prev, next) => {
  return (
    prev.trip === next.trip &&
    prev.columns === next.columns &&
    prev.colWidths === next.colWidths &&
    prev.rowFontSize === next.rowFontSize &&
    prev.isActive === next.isActive &&
    prev.isHovered === next.isHovered &&
    prev.detailLabel === next.detailLabel &&
    prev.getTripVehicleLabel === next.getTripVehicleLabel &&
    prev.onOpenTrip === next.onOpenTrip &&
    prev.onHoverTrip === next.onHoverTrip &&
    prev.onClearHover === next.onClearHover &&
    prev.onRefuelClick === next.onRefuelClick
  )
})

const TripsBox = ({ user, t, layout, viewport }) => {
  const [selectedTrip, setSelectedTrip] = useState(null)
  const [tripDetailOpen, setTripDetailOpen] = useState(false)
  const [vehiclesById, setVehiclesById] = useState({})
  const [tripListFullscreen, setTripListFullscreen] = useState(false)
  const [addTripOpen, setAddTripOpen] = useState(false)
  const [hoveredTrip, setHoveredTrip] = useState(null)
  const [openExportDropdown, setOpenExportDropdown] = useState(false)
  const [activeRow, setActiveRow] = useState(null)
  const tooltipTimerRef = useRef(null)

const [deleteTrip, setDeleteTrip] = useState(null)
const [deleteModalOpen, setDeleteModalOpen] = useState(false)
const [selectedReceipt, setSelectedReceipt] = useState(null)
const [receiptDetailOpen, setReceiptDetailOpen] = useState(false)

const {
  data: trips = [],
  isLoading: tripsLoading,
  error: tripsError,
  refetch: refreshTrips
} = useQuery({
  queryKey: ['tripbox-trips', user?.id],
  enabled: !!user?.id,
  staleTime: 0,
  refetchOnMount: true,
  refetchOnWindowFocus: true,
  queryFn: async () => {
    const res = await fetchWithAuth('/triplog/trips')
    if (!res.ok) throw new Error('tripbox-trips-fetch-failed')

    const data = await res.json()
    return Array.isArray(data) ? data : []
  }
})

const {
  data: receipts = [],
  refetch: refreshReceipts
} = useQuery({
  queryKey: ['tripbox-receipts', user?.id],
  enabled: !!user?.id,
  staleTime: 0,
  refetchOnMount: true,
  refetchOnWindowFocus: true,
  queryFn: async () => {
    const res = await fetchWithAuth(`/users/${user.id}/receipts`)
    if (!res.ok) throw new Error('tripbox-receipts-fetch-failed')

    const data = await res.json()
    return Array.isArray(data) ? data : []
  }
})

const handleTripSaved = useCallback(() => {
  refreshTrips()
  refreshReceipts()
}, [refreshTrips, refreshReceipts])

const [orderBy, setOrderBy] = useState(() => {
  return localStorage.getItem('tripOrderBy') || 'tripNumber'
})

const [order, setOrder] = useState(() => {
  return localStorage.getItem('tripOrder') || 'desc'
})

const [columns, setColumns] = useState(() => {
  const stored = localStorage.getItem('tripColumns')
  if (stored) return JSON.parse(stored)

  return DEFAULT_COLUMNS
})

  const [colWidths, setColWidths] = useState(() => {
    const stored = localStorage.getItem('tripColWidths')
    return stored ? JSON.parse(stored) : {}
  })

  const sensors = useSensors(useSensor(PointerSensor))

  const handleSort = (col) => {
    if (orderBy === col) {
      setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setOrderBy(col)
      setOrder('asc')
    }
  }

  const handleMouseDown = (e, key) => {
    const startX = e.clientX
    const startWidth = colWidths[key] || 150

    const onMouseMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX
      const newWidth = Math.max(80, startWidth + delta)
      setColWidths(prev => ({ ...prev, [key]: newWidth }))
    }

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }

    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
  }

  const handleDragEnd = (event) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = columns.findIndex(c => c.key === active.id)
    const newIndex = columns.findIndex(c => c.key === over.id)

    const reordered = [...columns]
    const [moved] = reordered.splice(oldIndex, 1)
    reordered.splice(newIndex, 0, moved)

    setColumns(reordered)
  }

  const [tooltipEnabled, setTooltipEnabled] = useState(() => {
    const stored = localStorage.getItem('tooltipEnabled')
    return stored === null ? false : stored === 'true'
  })
  const { showSearch, setShowSearch, showFilter, setShowFilter } = useSharedModalUiState()
  const [searchQuery, setSearchQuery] = useState('')

  const [showFullscreenHint, setShowFullscreenHint] = useState(false)
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 })

  useEffect(() => {
    localStorage.setItem('tooltipEnabled', tooltipEnabled)
  }, [tooltipEnabled])

  useEffect(() => {
    localStorage.setItem('tripColumns', JSON.stringify(columns))
  }, [columns])

  useEffect(() => {
    localStorage.setItem('tripColWidths', JSON.stringify(colWidths))
  }, [colWidths])

  useEffect(() => {
    localStorage.setItem('tripOrderBy', orderBy)
  }, [orderBy])

  useEffect(() => {
    localStorage.setItem('tripOrder', order)
  }, [order])

const loadVehicles = useCallback(async () => {
  if (!user?.id) return

  try {
    const res = await fetchWithAuth(`/users/${user.id}/vehicles`)
    if (!res.ok) return

    const data = await res.json()
    if (!Array.isArray(data)) return

    const index = data.reduce((acc, vehicle) => {
      if (vehicle?.id == null) return acc
      acc[String(vehicle.id)] = vehicle
      return acc
    }, {})

    setVehiclesById(index)
  } catch {}
}, [user?.id])

useEffect(() => {
  loadVehicles()
}, [loadVehicles])

  const getTripVehicleLabel = useCallback((trip) => {
    const vehicleFromList = vehiclesById[String(trip?.vehicleId)]
    const vehicle = vehicleFromList || trip?.vehicle
    return vehicle?.name || vehicle?.model || vehicle?.licensePlate || vehicle?.spz || '-'
  }, [vehiclesById])

const getTripVehicleSpz = useCallback((trip) => {
  const vehicleFromList = vehiclesById[String(trip?.vehicleId)]
  const vehicle = vehicleFromList || trip?.vehicle
  return vehicle?.licensePlate || vehicle?.spz || '-'
}, [vehiclesById])

  const getTripFilterData = useCallback((trip) => ({
    vehicleId: trip?.vehicleId,
    date: trip?.date
  }), [])

  const {
    criteria: filterCriteria,
    setCriteria: setFilterCriteria,
    filteredItems: filteredTrips
  } = useGenericListFilter(trips, getTripFilterData)

  const isFiltered = Object.values(filterCriteria || {}).some(v => v && v !== '')

  const vehicleOptions = useMemo(() => {
    return Object.values(vehiclesById).map((vehicle) => {
      const name = vehicle?.name || vehicle?.model || vehicle?.licensePlate || vehicle?.spz || `Vozidlo #${vehicle?.id}`
      const plate = vehicle?.licensePlate || vehicle?.spz || ''

      return {
        value: String(vehicle?.id),
        label: plate && name !== plate ? `${name} (${plate})` : name
      }
    })
  }, [vehiclesById])

  const getTripSearchData = useCallback((trip) => {
    const vehicleFromList = vehiclesById[String(trip?.vehicleId)]
    const vehicle = vehicleFromList || trip?.vehicle

    return {
      ...trip,
      vehicleName: vehicle?.name,
      vehicleModel: vehicle?.model,
      vehicleLicensePlate: vehicle?.licensePlate,
      vehicleSpz: vehicle?.spz,
      searchDate: trip?.date ? formatDateSk(trip.date) : '',
      searchStartTime: trip?.startTimeIso ? formatTimeSk(trip.startTimeIso) : '',
      searchEndTime: trip?.endTimeIso ? formatTimeSk(trip.endTimeIso) : ''
    }
  }, [vehiclesById])

  const searchFilteredTrips = useGenericTextSearch(
    filteredTrips,
    searchQuery,
    getTripSearchData
  )

  const sortedTrips = useMemo(() => {
    return [...searchFilteredTrips].sort((a, b) => {
      let aVal = a[orderBy]
      let bVal = b[orderBy]

      if (orderBy === 'vehicleId') {
        aVal = getTripVehicleLabel(a)
        bVal = getTripVehicleLabel(b)
      }

      if (orderBy === 'spz') {
        aVal = getTripVehicleSpz(a)
        bVal = getTripVehicleSpz(b)
      }

      if (orderBy === 'date' || orderBy === 'startTimeIso' || orderBy === 'endTimeIso') {
        aVal = aVal ? new Date(aVal).getTime() : 0
        bVal = bVal ? new Date(bVal).getTime() : 0
      }

      if (orderBy === 'distanceKm') {
        aVal = Number(aVal) || 0
        bVal = Number(bVal) || 0
      }

      if (orderBy === 'tripNumber' || orderBy === 'id') {
        aVal = Number(a.tripNumber ?? aVal) || 0
        bVal = Number(b.tripNumber ?? bVal) || 0
      }

      if (typeof aVal === 'string') aVal = aVal.toLowerCase()
      if (typeof bVal === 'string') bVal = bVal.toLowerCase()

      if (aVal > bVal) return order === 'asc' ? 1 : -1
      if (aVal < bVal) return order === 'asc' ? -1 : 1
      return 0
    })
  }, [searchFilteredTrips, orderBy, order, getTripVehicleLabel, getTripVehicleSpz])

  const isTableModified = useMemo(() => {
    const defaultKeys = DEFAULT_COLUMNS.map(c => c.key)
    const currentKeys = columns.map(c => c.key)

    const columnsChanged =
      JSON.stringify(defaultKeys) !== JSON.stringify(currentKeys)

    const widthsChanged = Object.keys(colWidths).length > 0

    const sortChanged =
      orderBy !== 'tripNumber' || order !== 'desc'

    return columnsChanged || widthsChanged || sortChanged
  }, [columns, colWidths, orderBy, order])

  const detailLabel = t('TRIPLOG.BUTTON.DETAIL')
  const detailColWidth = viewport <= 900 ? 110 : 50

  const filterLabels = useMemo(() => ({
    allVehicles: t('TRIPLOG.BUTTON.ALL_VEHICLES'),
    from: t('TRIPLOG.FILTER_FROM'),
    to: t('TRIPLOG.FILTER_TO'),
    month: t('TRIPLOG.FILTER_MONTH'),
    year: t('TRIPLOG.FILTER_YEAR'),
    clear: t('TRIPLOG.BUTTON.CLEAR')
  }), [t])

  const handleOpenTrip = useCallback((trip, markRow = false) => {
    if (markRow) setActiveRow(trip.id)
    setSelectedTrip(trip)
    setTripDetailOpen(true)
  }, [])

  const selectedTripIndex = useMemo(
    () => sortedTrips.findIndex((item) => item?.id === selectedTrip?.id),
    [sortedTrips, selectedTrip?.id]
  )

  const handlePrevTrip = useCallback(() => {
    if (selectedTripIndex <= 0) return
    setSelectedTrip(sortedTrips[selectedTripIndex - 1])
    setActiveRow(sortedTrips[selectedTripIndex - 1]?.id ?? null)
  }, [selectedTripIndex, sortedTrips])

  const handleNextTrip = useCallback(() => {
    if (selectedTripIndex < 0 || selectedTripIndex >= sortedTrips.length - 1) return
    setSelectedTrip(sortedTrips[selectedTripIndex + 1])
    setActiveRow(sortedTrips[selectedTripIndex + 1]?.id ?? null)
  }, [selectedTripIndex, sortedTrips])

  const handleHoverTrip = useCallback((trip) => {
    setHoveredTrip((prev) => {
      if (prev?.id === trip?.id) return prev
      return trip
    })
  }, [])

  const handleClearHoveredTrip = useCallback(() => {
    setHoveredTrip((prev) => (prev == null ? prev : null))
  }, [])

const handleDeleteTripClick = useCallback((trip) => {
  setDeleteTrip(trip)
  setDeleteModalOpen(true)
}, [])

const handleRefuelClick = useCallback((trip) => {
  const tripReceiptId = trip?.receiptId ?? trip?.receipt_id
  if (!tripReceiptId) return

  const assignedReceipt = receipts.find((receipt) => String(receipt.id) === String(tripReceiptId))
  if (!assignedReceipt) return

  setSelectedReceipt(assignedReceipt)
  setReceiptDetailOpen(true)
}, [receipts])

  return (
    <div
      style={{
        background: '#ededed',
        padding: 12,
        border: '1.5px solid #1e3f44',
        borderRadius: 10,
        width: '100%',
        boxSizing: 'border-box',
        position: 'relative',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}
      >

	<GenericAddButton
  	onToggle={() => setAddTripOpen(true)}
	/>

        <GenericSearchToggle
          visible={showSearch}
          query={searchQuery}
          onQueryChange={setSearchQuery}
          onToggle={() => {
            setShowSearch(prev => {
              if (prev) setSearchQuery('')
              return !prev
            })
          }}
          placeholder={t('TRIPLOG.SEARCH_PLACEHOLDER', { defaultValue: 'Hľadať...' })}
        />

        <FilterToggleButton
          visible={showFilter}
          active={isFiltered}
          onToggle={() => setShowFilter(prev => !prev)}
        />

        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            cursor: 'pointer',
            fontSize: 13,
            userSelect: 'none',
            marginTop: 5
          }}
        >
          <input
            type="checkbox"
            checked={tooltipEnabled}
            onChange={(e) => setTooltipEnabled(e.target.checked)}
            style={{
              cursor: 'pointer',
              appearance: 'none',
              width: 16,
              height: 16,
              border: '1px solid #1e3f44',
              borderRadius: 3,
              background: tooltipEnabled ? '#2da9a4' : '#fff'
            }}
          />
          {t('TRIPLOG.SERVICECOSTS.TOGGLE')}
        </label>

<RefreshButton onClick={() => {
  refreshTrips()
  loadVehicles()
}} />

        <ResetTableButton
          isModified={isTableModified}
          filterOpen={showFilter}
          onReset={() => {
	    const resetCols = DEFAULT_COLUMNS

            setColumns(resetCols)
            setColWidths({})
	    setOrderBy('tripNumber')
	    setOrder('desc')

            localStorage.removeItem('tripColWidths')
            localStorage.removeItem('tripOrderBy')
            localStorage.removeItem('tripOrder')
          }}
        />
<button
  onMouseEnter={(e)=>{
    e.currentTarget.style.background='#2da9a4'
  }}
  onMouseLeave={(e)=>{
    e.currentTarget.style.background='#f5f5f5'
    e.currentTarget.style.transform='scale(1)'
    e.currentTarget.style.boxShadow='0 1px 8px rgba(0,0,0,0.30)'
  }}
  onMouseDown={(e)=>{
    e.currentTarget.style.transform='scale(0.96)'
    e.currentTarget.style.boxShadow='0 0px 4px rgba(0,0,0,0.25)'
  }}
  onMouseUp={(e)=>{
    e.currentTarget.style.transform='scale(1)'
    e.currentTarget.style.boxShadow='0 1px 8px rgba(0,0,0,0.30)'
  }}
onClick={(e)=>{
  e.currentTarget.blur()
  setOpenExportDropdown(true)
  setTripListFullscreen(true)
}}
  style={{
    background:'#f5f5f5',
    border:'1px solid #1e3f44',
    borderRadius:6,
    height:24,
    padding:'0 10px',
    cursor:'pointer',
    fontWeight:700,
    boxShadow:'0 1px 8px rgba(0,0,0,0.30)',
    transition:'transform 0.05s ease, box-shadow 0.1s ease',
    marginTop:5
  }}
>
          {t('TRIPLOG.BUTTON_EXPORT')}
</button>

<button
onMouseEnter={(e) => {
  const timer = setTimeout(() => {
    setShowFullscreenHint(t('MODAL_DETAILS'))
  }, 600)

  tooltipTimerRef.current = timer
}}
  onMouseLeave={(e) => {
    if (tooltipTimerRef.current) {
  clearTimeout(tooltipTimerRef.current)
  tooltipTimerRef.current = null
}

    setShowFullscreenHint(false)
  }}
  onMouseMove={(e) => setCursorPos({ x: e.clientX, y: e.clientY })}
  onMouseDown={(e) => {
    e.currentTarget.style.transform = 'scale(0.96)'
    e.currentTarget.style.boxShadow = '0 0px 4px rgba(0,0,0,0.25)'
  }}
  onMouseUp={(e) => {
    e.currentTarget.style.transform = 'scale(1)'
    e.currentTarget.style.boxShadow = 'none'
  }}
  onClick={(e) => {
    if (tooltipTimerRef.current) {
  clearTimeout(tooltipTimerRef.current)
  tooltipTimerRef.current = null
}

    e.currentTarget.blur()
    setTripListFullscreen(true)
  }}
  style={{
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: 32
  }}
>
  ⛶
</button>
      </div>

      <h3 style={{ fontSize: 18, marginTop: 0, marginBottom: 10 }}>
        {t('TRIPLOG.TAB.TRIPS')}
      </h3>

      {tripsLoading && trips.length === 0 && <div>Načítavam...</div>}
      {tripsError && <div style={{ color: 'red' }}>{t('common.error')}</div>}

      {showFilter && (
        <GenericListFilter
          value={filterCriteria}
          onChange={setFilterCriteria}
          vehicles={vehicleOptions}
          labels={filterLabels}
        />
      )}

<div style={{
  border: '1px solid #c7c7c7',
  borderRadius: 10,
  background: '#fff',
  flex: 1,
  minHeight: 0,
  maxWidth: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden'
}}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={columns.map(c => c.key)}
            strategy={horizontalListSortingStrategy}
          >
<div
  style={{
    display: 'flex',
    background: '#c8e1e1',
    padding: '8px 12px',
    fontWeight: 700,
    fontSize: layout.vehicleHeaderFontSize
  }}
>
              {columns.map(col => (
                <SortableHeaderCell
                  key={col.key}
                  col={col}
                  orderBy={orderBy}
                  order={order}
                  handleSort={handleSort}
                  colWidths={colWidths}
  		  handleMouseDown={handleMouseDown}
 		  t={t}
                />
              ))}
<div
  onMouseEnter={(e) => {
    const timer = setTimeout(() => {
      setShowFullscreenHint(t('TRIPLOG.BUTTON.REFUEL'))
    }, 600)
    tooltipTimerRef.current = timer
  }}
  onMouseLeave={() => {
    if (tooltipTimerRef.current) {
  clearTimeout(tooltipTimerRef.current)
  tooltipTimerRef.current = null
}
    setShowFullscreenHint(false)
  }}
  onMouseMove={(e) => setCursorPos({ x: e.clientX, y: e.clientY })}
  style={{
    flex: `0 0 ${detailColWidth}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }}
>
  <img
    src={refuelingIcon}
    alt="refueling"
    style={{ width: 23, height: 23 }}
  />
</div>

<div
  onMouseEnter={(e) => {
    const timer = setTimeout(() => {
      setShowFullscreenHint(t('TRIPLOG.DETAIL_TITLE'))
    }, 600)
    tooltipTimerRef.current = timer
  }}
  onMouseLeave={() => {
    if (tooltipTimerRef.current) {
  clearTimeout(tooltipTimerRef.current)
  tooltipTimerRef.current = null
}
    setShowFullscreenHint(false)
  }}
  onMouseMove={(e) => setCursorPos({ x: e.clientX, y: e.clientY })}
  style={{
    flex: `0 0 ${detailColWidth}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }}
>
  <img
    src={detailIcon}
    alt="detail"
    style={{ width: 22, height: 22 }}
  />
</div>
<div
  onMouseEnter={(e) => {
    const timer = setTimeout(() => {
      setShowFullscreenHint(t('TRIPLOG.DELETE_TRIP'))
    }, 600)
    tooltipTimerRef.current = timer
  }}
  onMouseLeave={() => {
    if (tooltipTimerRef.current) {
  clearTimeout(tooltipTimerRef.current)
  tooltipTimerRef.current = null
}
    setShowFullscreenHint(false)
  }}
  onMouseMove={(e) => setCursorPos({ x: e.clientX, y: e.clientY })}
  style={{
    flex: `0 0 ${detailColWidth}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }}
>
  <img
    src={deleteIcon}
    alt="delete"
    style={{
      width: 22,
      height: 22
    }}
  />
</div>
            </div>
          </SortableContext>
        </DndContext>
<div
  style={{
    overflow: 'auto',
    overflowX: 'hidden',
    flex: 1,
    minHeight: 0
  }}
>
{sortedTrips.map(trip => (
  <TripRow
    key={trip.id}
    trip={trip}
    columns={columns}
    colWidths={colWidths}
    rowFontSize={layout.vehicleRowFontSize}
    isActive={activeRow === trip.id}
    isHovered={hoveredTrip?.id === trip.id}
    detailLabel={detailLabel}
    detailColWidth={detailColWidth}
    onDeleteTrip={handleDeleteTripClick}
    getTripVehicleLabel={getTripVehicleLabel}
    getTripVehicleSpz={getTripVehicleSpz}
    onOpenTrip={handleOpenTrip}
    onHoverTrip={handleHoverTrip}
    onClearHover={handleClearHoveredTrip}
    onRefuelClick={handleRefuelClick}
setShowFullscreenHint={setShowFullscreenHint}
tooltipTimerRef={tooltipTimerRef}
setCursorPos={setCursorPos}
t={t}
  />
))}
      </div>
</div>
      {tooltipEnabled && hoveredTrip && (
        <div
          className="vehicle-tooltip"
          onMouseLeave={handleClearHoveredTrip}
          style={{
            position: 'fixed',
            right:
              viewport >= 1920 ? 9 :
              viewport >= 1600 ? 9 :
              viewport >= 1366 ? 10 : 20,
            bottom:
              viewport >= 1920 ? 50 :
              viewport >= 1600 ? 50 :
              viewport >= 1366 ? 49 : 30,
            fontSize:
              viewport >= 1920 ? 14 :
              viewport >= 1600 ? 14 :
              viewport >= 1366 ? 13 : 13,
            maxHeight:
              viewport >= 1920 ? '80vh' :
              viewport >= 1600 ? '75vh' : '96vh',
            background: '#f6ffff',
            border: '1px solid #1e3f44',
            borderRadius: 8,
            padding: '16px 20px',
            boxShadow: '0 8px 20px rgba(0,0,0,0.35)',
            zIndex: 9999,
            overflowY: 'auto'
          }}
        >
          {tripDetailFields.map(([key, labelKey]) => (
            <div key={key} style={{ marginBottom: 4, display: 'flex' }}>
              <strong style={{ minWidth: 220 }}>
                {t(labelKey)}:
              </strong>
              <span style={{ color: '#1e3f44', fontWeight: 600 }}>
{key === 'vehicleId'
  ? getTripVehicleLabel(hoveredTrip)
  : key === 'spz'
    ? getTripVehicleSpz(hoveredTrip)
    : key === 'date'
      ? formatDateSk(hoveredTrip.date)
      : key === 'startTimeIso' || key === 'endTimeIso'
        ? formatDateTimeSk(hoveredTrip[key])
        : key === 'duration'
          ? (() => {
              const minutes = calculateTripDurationMinutes(
                hoveredTrip.startTimeIso,
                hoveredTrip.endTimeIso
              )

              if (minutes == null) return '-'

              const h = String(Math.floor(minutes / 60)).padStart(2, '0')
              const m = String(minutes % 60).padStart(2, '0')

              return `${h}:${m}`
            })()
          : key === 'avgSpeedKmh'
            ? (typeof hoveredTrip.avgSpeedKmh === 'number' && hoveredTrip.avgSpeedKmh > 0
                ? Number(hoveredTrip.avgSpeedKmh).toFixed(1)
                : '-')
            : key === 'fuelUsed'
              ? (() => {
                  const vehicle = vehiclesById[String(hoveredTrip.vehicleId)]
                  const liters = calculateFuelUsedLiters(
                    hoveredTrip,
                    vehicle ? [vehicle] : []
                  )
                  return liters != null ? liters.toFixed(2) : '-'
                })(): key === 'refuel'
  ? (() => {
      const id = hoveredTrip?.receiptId ?? hoveredTrip?.receipt_id
      if (!id) return '-'

      const receipt = receipts.find(r => String(r.id) === String(id))
      if (!receipt) return '-'

      return `${receipt.units ?? '-'} ${receipt.unit ?? ''} | ${receipt.total_price ?? '-'} ${receipt.currency ?? ''}`
    })()
              : key === 'mode'
                ? t(`TRIPLOG.MODES.${(hoveredTrip.mode || '').toUpperCase()}`)
                : hoveredTrip[key] && typeof hoveredTrip[key] === 'object'
                  ? `${hoveredTrip[key].latitude}, ${hoveredTrip[key].longitude}`
                  : hoveredTrip[key] ?? '-'}
              </span>
            </div>
          ))}
        </div>
      )}

      {showFullscreenHint && (
        <div
          style={{
            position: 'fixed',
    	    top: cursorPos.y - 50,
            left: cursorPos.x,
     	    transform: 'translateX(-50%)',
            background: 'rgba(0,0,0,0.85)',
            color: '#E7DFEC',
            padding: '6px 14px 8px 14px',
            borderRadius: 12,
            fontSize: 16,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 5000,
            border: '1.5px solid #1e3f44',
            boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
            display: 'flex',
            alignItems: 'center',
            lineHeight: 1
          }}
        >
{typeof showFullscreenHint === 'string'
  ? showFullscreenHint
  : t('MODAL_DETAILS')}
        </div>
      )}
<TripListModal
  open={tripListFullscreen}
  openExportDropdown={openExportDropdown}
  onClose={() => {
    setTripListFullscreen(false)
    setOpenExportDropdown(false)
  }}
  trips={trips}
  filterCriteria={filterCriteria}
  setFilterCriteria={setFilterCriteria}
/>
      {tripDetailOpen && (
<TripDetail
  open={tripDetailOpen}
  onClose={() => {
    setTripDetailOpen(false)
    setSelectedTrip(null)
    setActiveRow(null)
  }}
  trip={selectedTrip}
  onSaved={handleTripSaved}
  onPrevTrip={handlePrevTrip}
  onNextTrip={handleNextTrip}
  hasPrevTrip={selectedTripIndex > 0}
  hasNextTrip={selectedTripIndex >= 0 && selectedTripIndex < sortedTrips.length - 1}
/>
      )}
<TripAddModal
  open={addTripOpen}
  onClose={() => setAddTripOpen(false)}
  onSaved={() => {
    setAddTripOpen(false)
    handleTripSaved()
  }}
  userId={user?.id}
/>
<TripDeleteModal
  open={deleteModalOpen}
  trip={deleteTrip}
  onClose={() => {
    setDeleteModalOpen(false)
    setDeleteTrip(null)
  }}
  onDeleted={() => {
    setDeleteModalOpen(false)
    setDeleteTrip(null)
    refreshTrips()
  }}
  t={t}
/>
<ReceiptDetailModal
  open={receiptDetailOpen}
  onClose={() => {
    setReceiptDetailOpen(false)
    setSelectedReceipt(null)
  }}
  receipt={selectedReceipt}
  onSaved={(updatedReceipt) => {
    setSelectedReceipt(updatedReceipt)
    handleTripSaved()
  }}
/>
    </div>
  )
}

export default TripsBox